import React, { useState, useEffect } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const STORAGE_KEY = "ss_last_briefing";

interface StoredBriefing {
  text: string;
  post_count: number;
  top_platforms: string[];
  generated_at: string;
}

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}

export const WeeklyBriefing: React.FC = () => {
  const { session } = useAuth();
  const [briefing, setBriefing] = useState<StoredBriefing | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try { setBriefing(JSON.parse(stored)); } catch {}
    }
  }, []);

  const generate = async () => {
    if (!session?.access_token) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("weekly-briefing", {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      const stored: StoredBriefing = {
        text: data.briefing,
        post_count: data.post_count,
        top_platforms: data.top_platforms ?? [],
        generated_at: new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      setBriefing(stored);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate briefing");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border border-[#E5E7EB]">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold text-[#1A2E4A] flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#1A2E4A]" />
            Your Week in Review
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={generate}
            disabled={loading}
            className="text-xs"
          >
            {loading ? (
              <RefreshCw className="h-3 w-3 animate-spin mr-1" />
            ) : (
              <Sparkles className="h-3 w-3 mr-1" />
            )}
            {loading ? "Generating..." : "Generate briefing"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <p className="text-sm text-red-500 mb-3">{error}</p>
        )}
        {briefing ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{briefing.text}</p>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <span className="text-xs text-muted-foreground">{briefing.post_count} posts</span>
              {briefing.top_platforms.map((p) => (
                <Badge key={p} variant="secondary" className="text-xs capitalize">{p}</Badge>
              ))}
              <span className="text-xs text-muted-foreground ml-auto">
                Generated {relativeTime(briefing.generated_at)}
              </span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Generate your first weekly briefing — Claude reads your feed so you don't have to.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default WeeklyBriefing;
